import { faListCheck } from '@fortawesome/free-solid-svg-icons';
import { Controller, useForm } from 'react-hook-form';

import { Props as BlueprintFormProps, BlueprintFormValues } from '@/forms/BlueprintForm/types';
import FieldError from '@/forms/FieldError';
import { useCreateBlueprint, useTaskableTypes, useUpdateBlueprint } from '@/hooks/useBlueprint';
import useKustomerTags from '@/hooks/useKustomerTags';
import useStaff from '@/hooks/useStaff';
import useWorkGroups from '@/hooks/useWorkGroups';
import { Blueprint, BlueprintPayload } from '@/types/blueprint';
import { Button, Input, InputGroup, Select } from '@/ui';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import { OptionType } from '@/ui/components/Form/Select/types';
import Spinner from '@/ui/components/Spinner';

// Mirrors work.models.Item.Statuses -- the set a blueprint may pre-set on the
// tasks it creates.
const STATUS_OPTIONS: OptionType<string>[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'closed', label: 'Closed / Archived' }
];

const DURATION_PLACEHOLDER = 'DD HH:MM:SS';

interface InnerProps extends BlueprintFormProps {
  staffOptions: OptionType<string>[];
  tagOptions: OptionType<string>[];
  taskableTypeOptions: OptionType<string>[];
  workGroupOptions: OptionType<string>[];
}

const buildDefaultValues = (
  blueprint: Blueprint | undefined,
  {
    staffOptions,
    taskableTypeOptions,
    workGroupOptions
  }: Omit<InnerProps, keyof BlueprintFormProps | 'tagOptions'>
): BlueprintFormValues => {
  if (!blueprint) {
    return {
      assignedWorkGroup: null,
      assignee: null,
      description: '',
      dueDuration: '',
      key: '',
      name: '',
      snoozedDuration: '',
      status: null,
      tags: [],
      // Blueprints almost always target People; default to the first (and today
      // only) taskable type so the required field starts valid.
      taskableType: taskableTypeOptions[0] ?? null,
      title: ''
    };
  }

  const assigneeName = blueprint.assignee
    ? `${blueprint.assignee.first_name ?? ''} ${blueprint.assignee.last_name ?? ''}`.trim()
    : '';

  return {
    assignedWorkGroup:
      workGroupOptions.find((option) => option.value === blueprint.assigned_work_group) ?? null,
    assignee: blueprint.assignee
      ? {
          value: blueprint.assignee.id,
          label:
            assigneeName ||
            staffOptions.find((option) => option.value === blueprint.assignee?.id)?.label ||
            'Unknown'
        }
      : null,
    description: blueprint.description ?? '',
    dueDuration: blueprint.due_duration ?? '',
    key: blueprint.key,
    name: blueprint.name,
    snoozedDuration: blueprint.snoozed_duration ?? '',
    status: STATUS_OPTIONS.find((option) => option.value === blueprint.status) ?? null,
    tags: blueprint.tags.map((tag) => ({ value: tag.slug, label: tag.name })),
    taskableType:
      taskableTypeOptions.find((option) => option.value === String(blueprint.taskable_type)) ??
      null,
    title: blueprint.title
  };
};

const toPayload = (values: BlueprintFormValues): Partial<BlueprintPayload> => ({
  assigned_work_group: values.assignedWorkGroup?.value ?? null,
  assignee_id: values.assignee?.value ?? null,
  description: values.description,
  due_duration: values.dueDuration.trim() || null,
  key: values.key.trim(),
  name: values.name.trim(),
  snoozed_duration: values.snoozedDuration.trim() || null,
  status: values.status?.value ?? '',
  tags_slugs: values.tags.map((tag) => tag.value),
  taskable_type: Number(values.taskableType?.value),
  title: values.title
});

const BlueprintFormFields = ({
  blueprint,
  onCancel,
  onEditForm,
  onSuccess,
  staffOptions,
  tagOptions,
  taskableTypeOptions,
  workGroupOptions
}: InnerProps) => {
  const isEdit = !!blueprint?.id;
  const createBlueprint = useCreateBlueprint();
  const updateBlueprint = useUpdateBlueprint();
  const isPending = createBlueprint.isPending || updateBlueprint.isPending;

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm<BlueprintFormValues>({
    defaultValues: buildDefaultValues(blueprint, {
      staffOptions,
      taskableTypeOptions,
      workGroupOptions
    })
  });

  const onSubmit = handleSubmit((values) => {
    const payload = toPayload(values);
    if (isEdit) {
      updateBlueprint.mutate({ id: blueprint!.id, data: payload }, { onSuccess });
    } else {
      createBlueprint.mutate(payload, { onSuccess });
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <InputGroup>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field }) => (
            <Input {...field} id="blueprint-name" label="Name" placeholder="e.g. Follow up call" />
          )}
        />
        <FieldError message={errors.name?.message} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="key"
          control={control}
          rules={{ required: 'Key is required' }}
          render={({ field }) => (
            <Input
              {...field}
              id="blueprint-key"
              label="Key"
              placeholder="e.g. follow-up-call"
              helpText="Unique identifier referenced across the system. Change with care."
            />
          )}
        />
        <FieldError message={errors.key?.message} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="taskableType"
          control={control}
          rules={{ validate: (value) => !!value || 'Taskable type is required' }}
          render={({ field: { onChange, value } }) => (
            <Select
              label="Taskable type"
              options={taskableTypeOptions}
              selectedOption={value}
              onChange={onChange}
              placeholder="Select..."
              error={errors.taskableType?.message}
            />
          )}
        />
      </InputGroup>

      <InputGroup>
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="blueprint-title"
              label="Title"
              placeholder="Title of created tasks (supports {{ handlebars }})"
            />
          )}
        />
      </InputGroup>

      <InputGroup>
        <span className="mb-1 block text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
          Description
        </span>
        <Controller
          name="description"
          control={control}
          render={({ field: { onChange, value } }) => (
            <RichTextEditor
              value={value}
              onChange={onChange}
              placeholder="Describe the task. Supports {{ handlebars }} templating."
            />
          )}
        />
      </InputGroup>

      <div className="grid grid-cols-2 gap-3">
        <InputGroup>
          <Controller
            name="assignee"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                clearable
                label="Assignee"
                options={staffOptions}
                selectedOption={value}
                onChange={onChange}
                placeholder="Unassigned"
              />
            )}
          />
        </InputGroup>

        <InputGroup>
          <Controller
            name="assignedWorkGroup"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                clearable
                label="Work group"
                options={workGroupOptions}
                selectedOption={value}
                onChange={onChange}
                placeholder="None"
              />
            )}
          />
        </InputGroup>
      </div>

      <InputGroup>
        <Controller
          name="status"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              clearable
              label="Default status"
              options={STATUS_OPTIONS}
              selectedOption={value}
              onChange={onChange}
              placeholder="No default"
            />
          )}
        />
      </InputGroup>

      <div className="grid grid-cols-2 gap-3">
        <InputGroup>
          <Controller
            name="snoozedDuration"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="blueprint-snoozed-duration"
                label="Snoozed duration"
                placeholder={DURATION_PLACEHOLDER}
                helpText="How long created tasks stay snoozed."
              />
            )}
          />
        </InputGroup>

        <InputGroup>
          <Controller
            name="dueDuration"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                id="blueprint-due-duration"
                label="Due duration"
                placeholder={DURATION_PLACEHOLDER}
                helpText="How long until created tasks are due."
              />
            )}
          />
        </InputGroup>
      </div>

      <InputGroup>
        <Controller
          name="tags"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              multiple
              label="Tags"
              options={tagOptions}
              selectedOptions={value}
              onChange={onChange}
              placeholder="Select tags"
              helpText="Tags are synced from Kustomer; pick from existing tags."
            />
          )}
        />
      </InputGroup>

      <div className="flex items-center gap-2">
        <Button type="submit" isLoading={isPending} disabled={isPending}>
          {isEdit ? 'Save changes' : 'Create blueprint'}
        </Button>
        <Button type="button" variant="outline" color="neutral" onClick={onCancel}>
          Cancel
        </Button>
        {isEdit && onEditForm && (
          <Button
            type="button"
            variant="outline"
            className="ml-auto"
            leftIcon={faListCheck}
            onClick={onEditForm}>
            Edit form
          </Button>
        )}
      </div>
    </form>
  );
};

const BlueprintForm = ({ blueprint, onCancel, onEditForm, onSuccess }: BlueprintFormProps) => {
  const { data: taskableTypes, isPending: taskableTypesPending } = useTaskableTypes();
  const { data: staffResponse, isPending: staffPending } = useStaff();
  const { data: workGroupsResponse, isPending: workGroupsPending } = useWorkGroups();
  const { data: tags, isPending: tagsPending } = useKustomerTags();

  if (taskableTypesPending || staffPending || workGroupsPending || tagsPending) {
    return <Spinner />;
  }

  const taskableTypeOptions: OptionType<string>[] = (taskableTypes ?? []).map((type) => ({
    value: String(type.id),
    label: type.model
  }));

  const staffOptions: OptionType<string>[] = (staffResponse?.data ?? [])
    .map((staff) => ({
      value: staff.id,
      label: `${staff.first_name ?? ''} ${staff.last_name ?? ''}`.trim()
    }))
    .filter((option) => option.label);

  const workGroupOptions: OptionType<string>[] = (workGroupsResponse?.data?.results ?? []).map(
    (group) => ({ value: group.id, label: group.name })
  );

  const tagOptions: OptionType<string>[] = (tags ?? []).map((tag) => ({
    value: tag.slug,
    label: tag.name
  }));

  return (
    <BlueprintFormFields
      blueprint={blueprint}
      onCancel={onCancel}
      onEditForm={onEditForm}
      onSuccess={onSuccess}
      staffOptions={staffOptions}
      tagOptions={tagOptions}
      taskableTypeOptions={taskableTypeOptions}
      workGroupOptions={workGroupOptions}
    />
  );
};

export default BlueprintForm;
